import React, { useEffect, useState } from "react";
import {
  Box,
  Card as MuiBaseCard,
  CardContent,
  CardMedia,
  Typography,
  CardHeader,
  Avatar,
  CardActions,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from "@mui/material";
import projectIcon from "../../assets/icons/projectIcon.svg";
import { CardProps } from "../../types/interface";
import heartIcon from "../../assets/icons/Heart.svg";
import viewDetailsIcon from "../../assets/icons/viewDetails.svg";
import renameIcon from "../../assets/icons/rename.svg";
import copyToIcon from "../../assets/icons/copyTo.svg";
import moveToIcon from "../../assets/icons/moveTo.svg";
import deleteIcon from "../../assets/icons/delete.svg";
import downloadThisDataSetIcon from "../../assets/icons/downloadThisDataSet.svg";
import VisionStudioSvg from "../VisionStudioSvg";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  COPY_TO,
  DELETE,
  DOWNLOAD_THIS_DATA_SET,
  MOVE_TO,
  RENAME,
  VIEW_DETAILS,
  VIEW_DETAILS_MODAL,
} from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "../../store/slice/UISlice";
import {
  addFavourites,
  deleteProject,
  updateProject,
  downloadSampleProject,
  downloadProjectDataset,
} from "../../store/slice/ProjectSlice";
import { AppDispatch, RootState } from "../../store/store";
import { toast } from "react-toastify";
import { fetchProjects } from "../../store/slice/projectHandler";
import { fetchFavourites } from "../../store/slice/ProjectImagesUploadedSlice";
import { ReactSVG } from "react-svg";
import downloadButton from "../../assets/icons/download.svg";
import LoaderGIF from "../../assets/img/loader.gif";
import { useNavigate } from "react-router-dom";
import { AxiosProgressEvent } from "axios";

/**
 * Reusable Componet to display cards.
 *
 * @param {object} CardProps
 * @property {boolean} cardHeader
 * @property {boolean} cardContent
 * @property {boolean} cardAction
 * @property {ProjectItem} project
 * @property {string} imageUrl
 * @property {function} updateLiked
 *
 */

interface ProjectData {
  id: string;
  labels: number;
  images: number;
  annotations: number;
  models: number;
  imageUrl: string;
  thubmImageUrl: string;
  name: string;
  description: string;
  taskTypeId: string;
  domainTypeId: string;
  createdDate: string;
  customerId: string;
  liked: boolean;
  isSample?: boolean;
}

const VisionStudioCard: React.FC<CardProps> = ({
  cardHeader = true,
  cardContent = true,
  cardAction = true,
  project,
  projectLength,
  dialogOpen,
  setDialogOpen,
  imageUrl,
  updateLiked,
  handleBoxClick,
  pageName,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const MuiBaseCardRef = React.useRef<HTMLDivElement>(null);
  const [deletes, setDelete] = useState<boolean>(false);
  const [renames, setRenames] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState<string | null>(null);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [downloadingSample, setDownloadingSample] = useState<boolean>(false);
  const [previousName, setPreviousName] = useState(project?.name);
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const { tasks } = useSelector((state: RootState) => state.Tasks);
  const { userName } = useSelector((state: RootState) => state.auth);
  const projectCount = localStorage.getItem("projectCount");
  const handleSubmitRename = async () => {
    setIsSubmit(true);
    if (projectData && projectData?.name.length > 2) {
      try {
        setActive(true);
        const response = await dispatch(updateProject(projectData)).unwrap(); // Ensure you handle promise response
        toast.success("Project Updated successfully");
        setPreviousName(response.name);
        dispatch(fetchProjects());
        dispatch(fetchFavourites());
        setRenames(false);
        setIsSubmit(false);
        setActive(false);
      } catch (error: any) {
        let errorMessage: string;

        if (typeof error === "string") {
          // If the error is a string, use it directly.
          errorMessage = error;
        } else if (error && error.detail) {
          // If the error has a 'detail' property, use that.
          errorMessage = error.detail;
        } else {
          // Fallback error message.
          errorMessage = "Failed to update the project";
        }

        toast.error(errorMessage);
        setIsSubmit(false);
        setActive(false);
      }
    }
  };

  const findNameById = (id: string) => {
    const item = tasks.find((entry) => entry.id === id);
    return item ? item.name : undefined;
  };

  const handleNothing = () => {};

  const handleClickDelete = async () => {
    if (selectedId !== null) {
      try {
        setIsDelete(true);
        const response = await dispatch(deleteProject(selectedId)).unwrap(); // Ensure you handle promise response
        toast.success("Project deleted successfully");
        dispatch(fetchProjects());
        dispatch(fetchFavourites());
        setIsDelete(false);
      } catch (error: any) {
        let errorMessage: string;

        if (typeof error === "string") {
          // If the error is a string, use it directly.
          errorMessage = error;
        } else if (error && error.detail) {
          // If the error has a 'detail' property, use that.
          errorMessage = error.detail;
        } else {
          // Fallback error message.
          errorMessage = "Failed to delete the project";
        }

        toast.error(errorMessage);
        setIsDelete(false);
      }
    } else {
      toast.error("No project selected for deletion");
    }
    setDelete(false);
    localStorage.setItem("popupOpen", false.toString());
  };

  const handleDeleteClose = () => {
    setDelete(false);
    localStorage.setItem("popupOpen", false.toString());
  };

  const handleRenameClose = () => {
    setRenames(false);
    localStorage.setItem("popupOpen", false.toString());
  };

  // Build menu options based on isProjectOwner
  const actionBarItem = [
    {
      name: VIEW_DETAILS.name,
      value: VIEW_DETAILS.value,
      icon: viewDetailsIcon,
    },
    { name: RENAME.name, value: RENAME.value, icon: renameIcon },
    { name: DELETE.name, value: DELETE.value, icon: deleteIcon },
  ];

  function stringAvatar(name: string) {
    if (!name || typeof name !== "string") {
      return { children: "?" }; // Default fallback
    }

    const nameParts = name.trim().split(" ");
    const initials =
      nameParts.length > 1
        ? `${nameParts[0][0]}${nameParts[1][0]}` // Use first letters of two words
        : `${nameParts[0][0]}`; // Use the first letter of a single word

    return {
      children: initials.toUpperCase(),
    };
  }
  //console.log("project length"+projectLength);
  // Open the dialog if the limit is reached
  const [refreshKey, setRefreshKey] = useState(0);
  const [downloadingIds, setDownloadingIds] = useState<string[]>([]);

  const handleDownload = (projectName: string) => {
    //console.log(projectCount);
    if ((projectLength ?? 0) >= (Number(projectCount) ?? 0)) {
      setDialogOpen?.(true);
      return;
    }
    setDownloadingSample(true);
    dispatch(downloadSampleProject({ data: { projectName } }))
      .unwrap()
      .then(() => {
        setDownloadingSample(false);
        console.log("successfull download sample project..");
      })
      .catch((error: any) => {
        toast.error(error.detail);
      });
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const handleSvgClick = (
    event:
      | React.MouseEvent<HTMLElement>
      | React.MouseEvent<SVGSVGElement, MouseEvent>,
    id: string
  ) => {
    event.stopPropagation();
    const target = event.currentTarget;
    target.classList.add("pulse-animation");
    target.addEventListener(
      "animationend",
      () => {
        target.classList.remove("pulse-animation");
      },
      { once: true } // Ensure the event listener is removed after it runs once
    );

    const likes = project && project.liked;
    dispatch(addFavourites({ ...project, liked: !likes }))
      .unwrap()
      .then(() => dispatch(fetchFavourites()))
      .catch((error: any) => {
        let errorMessage: string;

        if (typeof error === "string") {
          // If the error is a string, use it directly.
          errorMessage = error;
        } else if (error && error.detail) {
          // If the error has a 'detail' property, use that.
          errorMessage = error.detail;
        } else {
          // Fallback error message.
          errorMessage = "Failed to add to favourites";
        }

        toast.error(errorMessage);
      });

    if (updateLiked) {
      updateLiked(id);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLocal = (datasd: any) => {
    let chk = JSON.stringify(datasd);
    localStorage.setItem("selectedProject", chk);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    action: string
  ) => {
    event.stopPropagation();

    if (action == "delete") {
      setDelete(true);
      localStorage.setItem("popupOpen", true.toString());
    }
    if (action == "rename") {
      setRenames(true);
      localStorage.setItem("popupOpen", true.toString());
    }
    if (action === VIEW_DETAILS.value) {
      project &&
        dispatch(openModal({ name: VIEW_DETAILS_MODAL, project: project }));
      event.stopPropagation();
      handleClose();
      return false;
    }
    handleClose();
  };

  const open = Boolean(anchorEl);

  const taskId: string | undefined = project?.taskTypeId;
  const taskName = taskId ? findNameById(taskId) : "Invalid ID";

  const handleDownloadProjectDataset = async (
    projectId: string,
    projectName: string
  ) => {
    if (!projectId) {
      console.error("No project ID provided!");
      return;
    }

    // Mark as downloading
    setDownloadingIds((prev: any) => [...prev, projectId]);

    try {
      const resultAction = await dispatch(
        downloadProjectDataset({
          data: { projectId },
          options: {
            onDownloadProgress: (progressEvent) => {
              const total = progressEvent.total || 0;
              const current = progressEvent.loaded || 0;
              const percentage = Math.floor((current / total) * 100);
              console.log(`Download progress: ${percentage}%`);
            },
          },
        })
      );

      // Handle success
      if (downloadProjectDataset.fulfilled.match(resultAction)) {
        toast.success("Project dataset downloaded successfully", {
          position: "bottom-right",
        });
      } else {
        toast.error(
          resultAction.error?.message || "Failed to download dataset",
          {
            position: "bottom-right",
          }
        );
      }
    } catch (error: any) {
      toast.error(
        error?.detail || error?.message || "Failed to download dataset",
        { position: "bottom-right" }
      );
    }

    // Remove project ID after completion
    setDownloadingIds((prev: any) =>
      prev.filter((id: any) => id !== projectId)
    );

    handleClose();
  };

  return (
    <Box
      sx={{
        background: project ? "#fff" : "transparent",
        border: "transparent",
        height: "100%",

        borderRadius: "5%",
        boxShadow: project ? "0px 4px 10px rgba(0, 0, 0, 0.1)" : "none",
        cursor: "pointer",
        transition: "transform 0.3s ease, box-shadow 0.3s ease", // Smooth transition
        "&:hover": {
          transform: project ? "translateY(-5px)" : "", // Move the box up by 5 pixels
          boxShadow: project ? "0 0 15px rgba(0, 123, 255, 0.5)" : "", // Enhance the shadow on hover
        },
      }}
    >
      <div>
        <Dialog
          open={deletes}
          onClose={(e) => {
            setDelete(false);
            localStorage.setItem("popupOpen", false.toString());
          }}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">{"Confirm Delete"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete this item? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              id="calcelDeleteProject"
              onClick={handleDeleteClose}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              id="confirmDeleteProject"
              onClick={isDelete ? handleNothing : handleClickDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <div>
        <Dialog
          open={renames}
          onClose={handleRenameClose}
          aria-labelledby="rename-dialog-title"
          aria-describedby="rename-dialog-description"
        >
          <DialogTitle id="rename-dialog-title">{"Rename Project"}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              // id="fullWidth"
              autoFocus
              margin="dense"
              id="projectName"
              // label="Project Name"
              sx={{
                border:
                  isSubmit && projectData && projectData?.name.length < 3
                    ? "1px solid red"
                    : "0",
                width: "400px",
                background: "#F5F6F9",
                borderRadius: "4px",
                "& .MuiOutlinedInput-root": {
                  "& input": {
                    color: "#666", // Target the input text color
                  },
                  border: "none",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
              }}
              type="text"
              // variant="outlined"
              value={projectData?.name}
              onChange={(e) => {
                const val = e.target.value;
                const fina = val
                  .trimStart()
                  .slice(0, 50)
                  .replace(/\s{2,}/g, " ");
                setProjectData((prevData) =>
                  prevData ? { ...prevData, name: fina } : null
                );
              }}
            />
            <br />
            {isSubmit && projectData && projectData?.name.length < 3 ? (
              <span style={{ color: "red", fontSize: "10px" }}>
                Minimum 3 charcaters
              </span>
            ) : (
              ""
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRenameClose} color="primary">
              Cancel
            </Button>

            <Button
              onClick={isSubmit && active ? handleNothing : handleSubmitRename}
              color="primary"
              variant="contained"
              disabled={
                previousName?.toLowerCase() === projectData?.name?.toLowerCase()
              }
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <Box
        id={project?.id === "sample-project" ? "downloadProject" : ""}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: "relative",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor:
            project?.id === "sample-project" ? "rgba(0, 0, 0, 0.3)" : "", // Black with 30% opacity
          borderRadius: "10px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <MuiBaseCard
          ref={MuiBaseCardRef}
          onClick={(e) => {
            if (project?.isSample && project?.id === "sample-project") {
              handleDownload(project?.name || "sample-project");
            } else {
              handleBoxClick && handleBoxClick(e, project, MuiBaseCardRef);
              handleLocal(project);
            }
          }}
          sx={{
            background: "transparent",
            position: "relative",
            height: "100%",
            border: "none",
            boxShadow: "none", // Removes any shadow that might look like a border
          }}
        >
          {/* Project Header */}
          {cardHeader && (
            <CardHeader
              sx={{
                display: "flex",
                overflow: "hidden",
                padding: "13px 13px 8px 13px",
              }}
              avatar={
                <Avatar
                  sx={{
                    fontSize: "14px",
                    color: "black",
                    backgroundColor:
                      project && project.colour ? project.colour : "#CDF4DD",
                  }}
                  {...stringAvatar(project?.name ?? "Vision Studio")}
                />
              }
              title={
                <>
                  <Typography
                    noWrap
                    sx={{
                      overflow: "hidden",
                      fontSize: "15px",
                      lineHeight: "18px",
                      color: "#000000",
                    }}
                    variant="h4"
                  >
                    <abbr
                      title={project?.name}
                      style={{ textDecoration: "none" }}
                    >
                      {project && project.name?.length > 15
                        ? project.name.slice(0, 15) + "..."
                        : project &&
                          project.name.charAt(0).toUpperCase() +
                            project.name.slice(1)}
                    </abbr>
                  </Typography>
                  <p style={{ margin: "0", fontSize: "10px", opacity: "0.5" }}>
                    {taskName}
                  </p>
                </>
              }
              /* subheader={project?.category} */
              action={
                <div>
                  {!project?.isNotDownload && (
                    <IconButton
                      id="moreOptions"
                      aria-label="settings"
                      onClick={(e) => handleClick(e)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    PaperProps={{
                      style: {
                        boxShadow: "none",
                      },
                    }}
                    PopoverClasses={{
                      root: "custom-popover",
                    }}
                    BackdropProps={{ invisible: true }}
                    disableScrollLock
                    MenuListProps={{
                      style: { paddingBottom: "0" },
                    }}
                  >
                    {/* Menu options based on isProjectOwner */}
                    {project?.isProjectOwner ? (
                      <>
                        {actionBarItem?.map(
                          (menuItem, index) =>
                            !(
                              menuItem.value !== "delete" && project?.isSample
                            ) && (
                              <MenuItem
                                key={index}
                                onClick={(e) => {
                                  handleMenuItemClick(e, menuItem.value);
                                  setSelectedId(project ? project?.id : null);
                                  setProjectData(project ? project : null);
                                }}
                              >
                                <ListItemIcon>
                                  <VisionStudioSvg
                                    icon={menuItem.icon}
                                    color="#000000"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primaryTypographyProps={{
                                    fontWeight:
                                      theme.typography.fontWeightRegular,
                                    fontSize: theme.typography.fontSize,
                                    color: "#000000",
                                  }}
                                  primary={menuItem.name}
                                />
                              </MenuItem>
                            )
                        )}
                        {/* Download Project Dataset Option */}
                        {!project?.isSample && (
                          <MenuItem
                            onClick={() => {
                              if (project?.id) {
                                handleDownloadProjectDataset(
                                  project.id,
                                  project.name
                                );
                              }
                            }}
                            className={
                              downloadingIds.includes(project?.id || "")
                                ? "menu-item-downloading"
                                : ""
                            }
                            disabled={downloadingIds.includes(
                              project?.id || ""
                            )}
                          >
                            <ListItemIcon>
                              {downloadingIds.includes(project?.id || "") ? (
                                <img
                                  src={LoaderGIF}
                                  alt="loading..."
                                  style={{ width: 20, height: 20 }}
                                />
                              ) : (
                                <VisionStudioSvg
                                  icon={downloadThisDataSetIcon}
                                  color="#000000"
                                />
                              )}
                            </ListItemIcon>

                            <ListItemText
                              primaryTypographyProps={{
                                fontWeight: theme.typography.fontWeightRegular,
                                fontSize: theme.typography.fontSize,
                                color: "#000000",
                              }}
                              primary={
                                downloadingIds.includes(project?.id || "")
                                  ? "Downloading..."
                                  : "Download Project Dataset"
                              }
                            />
                          </MenuItem>
                        )}
                      </>
                    ) : (
                      <MenuItem
                        onClick={(e) => {
                          handleMenuItemClick(e, VIEW_DETAILS.value);
                          setSelectedId(project ? project?.id : null);
                          setProjectData(project ? project : null);
                        }}
                      >
                        <ListItemIcon>
                          <VisionStudioSvg
                            icon={viewDetailsIcon}
                            color="#000000"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{
                            fontWeight: theme.typography.fontWeightRegular,
                            fontSize: theme.typography.fontSize,
                            color: "#000000",
                          }}
                          primary={VIEW_DETAILS.name}
                        />
                      </MenuItem>
                    )}
                  </Menu>
                </div>
              }
              subheaderTypographyProps={{
                color: "#000000DE",
                fontWeight: theme.typography.h5.fontWeight,
                fontSize: theme.fontSize.smallText,
                lineHeight: "15px",
              }}
            />
          )}

          {/* Project Media */}
          <Box
            sx={{
              display: "flex", // Use Flexbox for the container
              flexWrap: "wrap", // Allow cards to wrap if they overflow
              justifyContent: "space-between", // Distribute space between cards
            }}
          >
            <Box
              // key={project.id}
              sx={{
                border: "none",
                position: "relative",
                display: "inline-block",
                margin: "3px",
                cursor: "pointer",
                flex: "1 1 calc(33.33% - 6px)", // Adjust based on desired number of cards per row
                height: "100%", // Ensures all boxes take the same height
                "&:hover::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "20px",
                  background: project
                    ? ""
                    : "linear-gradient(to bottom, rgba(0, 0, 0, 0.5), transparent)",
                  zIndex: 1,
                },
              }}
            >
              {project ? (
                <>
                  {project.isSample && project?.id == "sample-project" ? (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "8rem",
                      }}
                    >
                      {/* CardMedia for the image */}
                      <CardMedia
                        component="img"
                        image={
                          project.thubmImageUrl.includes("/api")
                            ? `${process.env.REACT_APP_SERVER_BASEURL}${project.thubmImageUrl}`
                            : `${project.thubmImageUrl}`
                        }
                        alt=""
                        sx={{
                          width: "100%",
                          height: "8rem", // Fixed height for uniformity
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                      />

                      {/* Overlay with centered button */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "rgba(0, 0, 0, 0.3)", // Black with 30% opacity
                          display: "flex", // Flexbox for centering
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {/* Centered Download Button */}
                        {downloadingSample ? (
                          <img
                            src={LoaderGIF} // Correct syntax for including a dynamic image
                            alt="loading.."
                            style={{
                              width: "20px", // Adjust size as needed
                              height: "20px",
                            }}
                          />
                        ) : (
                          isHovered && (
                            <>
                              <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                  backgroundColor: "#1565c0", // Slightly transparent white
                                  color: "white",
                                  padding: "0.5rem 1rem",
                                  textTransform: "none",
                                  borderRadius: "8px",
                                  "&:hover": {
                                    backgroundColor: "rgb(36, 88, 219)", // Fully opaque on hover
                                  },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem", // Space between text and icon
                                }}
                                onClick={() => {
                                  // Add your download logic here
                                  console.log("Download clicked");
                                }}
                              >
                                Download Sample
                                <img
                                  src={downloadButton} // Correct syntax for including a dynamic image
                                  alt="Download Icon"
                                  style={{
                                    width: "20px", // Adjust size as needed
                                    height: "20px",
                                  }}
                                />
                              </Button>
                            </>
                          )
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <CardMedia
                      component="img"
                      image={`${process.env.REACT_APP_SERVER_BASEURL}${project.thubmImageUrl}`}
                      alt=""
                      sx={{
                        width: "100%",
                        height: "8rem", // Fixed height for uniformity
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                    />
                  )}
                </>
              ) : (
                <Avatar
                  src={`${process.env.REACT_APP_SERVER_BASEURL}${imageUrl}`}
                  variant="square"
                  sx={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                    margin: "3px",
                  }}
                />
              )}
            </Box>
          </Box>

          <Box>
            {/* Project Details */}

            {cardContent && (
              <CardContent sx={{ padding: "10px" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#00000099",
                    marginBottom: "5px",
                    fontSize: "0.7rem",
                    fontWeight: theme.typography.fontWeightRegular,
                  }}
                >
                  Images: {project?.images} | Labels: {project?.labels} |
                  Annotations: {project?.annotations}
                </Typography>
              </CardContent>
            )}
            <div style={{ height: "20px" }}></div>
            {cardAction && (
              <CardActions
                disableSpacing
                sx={{
                  paddingTop: "20px",
                  display: "flex",
                  justifyContent: "end",
                  position: "absolute", // Make the element absolute
                  bottom: "0", // Align to the bottom
                  right: "0", // Align to the right
                  width: "100%", // Optional, ensures full card width alignment if needed
                  // Add padding to prevent overlap
                }}
              >
                {project && project?.id !== "sample-project" && (
                  <>
                    <ReactSVG
                      id={
                        pageName === "favour" ? "favourPage" : "favourProject"
                      }
                      style={{ alignSelf: "end", cursor: "pointer" }}
                      src={heartIcon}
                      onClick={(e) => handleSvgClick(e, project.id)}
                      beforeInjection={(svg: SVGSVGElement) => {
                        if (svg) {
                          const path = svg.querySelector("path");
                          if (path) {
                            path.setAttribute(
                              "fill",
                              project.liked ? "#73A9F8" : "#5F6368"
                            );
                          }
                        }
                      }}
                    />
                  </>
                )}
              </CardActions>
            )}
          </Box>
        </MuiBaseCard>
      </Box>
    </Box>
  );
};

export default React.memo(VisionStudioCard);
